/**
 * Core auth types — shared across @linkhr/auth modules and public consumers.
 *
 * These types describe the on-wire / in-memory shape of the authenticated
 * session. The JWT access token is decoded (never verified) on the client to
 * surface `AuthClaims`; signature trust is the backend's responsibility.
 */

export type Audience = "admin" | "mobile" | "platform" | "accounting";

export interface AuthClaims {
  /** Subject — user id. */
  sub: string;
  /** Tenant id or `null` for `platform` audience. */
  tenant_id: string | null;
  /** Audience — drives which API surface the token targets. */
  aud: Audience;
  /** Role codes (RBAC). */
  roles: string[];
  /** Issued-at (seconds since epoch). */
  iat: number;
  /** Expiry (seconds since epoch). */
  exp: number;
  /** CC-58 — account email. Optional: older tokens may not carry it. */
  email?: string;
  /** CC-58 — display name (falls back to email local-part backend-side). */
  name?: string;
}

export interface AuthUser {
  id: string;
  /** Empty for an employee who signs in with a username and has no email. */
  email: string;
  displayName: string;
  /** `<PREFIX>-<employee number>` for a username sign-in; null/absent otherwise. */
  username?: string | null;
}

export interface AuthSnapshot {
  isAuthenticated: boolean;
  user: AuthUser | null;
  /**
   * The JWT `sub` — the USER id, and the only id backend audit fields
   * (`uploadedBy`, `decidedBy`, `createdBy`, …) are ever expressed in.
   *
   * `user.id` is NOT it. It starts as `sub` and is then overwritten by
   * `hydrateUserFromMe` with the EMPLOYEE id from `/employees/me`, so any
   * screen comparing `user.id` against an audit field compares two different
   * namespaces and silently never matches — "a colleague uploaded this",
   * shown to the person who uploaded it. Compare against this instead.
   */
  userId: string | null;
  roles: readonly string[];
  tenantId: string | null;
  aud: Audience | null;
  accessToken: string | null;
  /**
   * Backend-issued effective permission codes (`module:action[:scope]`).
   * `undefined` = not yet hydrated (cold-boot before `fetchEffectivePermissions`
   * resolves); `Set` = hydrated (possibly empty for newly-provisioned users).
   *
   * Consumers gate via `hasPermission(snap, code)` or `requirePermission()`.
   * Hydration happens after every login + refresh per Plan 08.1-02 (CC-87 /
   * be-094 contract).
   */
  effectivePermissions: ReadonlySet<string> | undefined;
  /**
   * True while the account holds a temporary password HR issued. The access
   * token then opens only `POST /me/password`, refresh and logout; every other
   * call answers 403 AUTH_PASSWORD_CHANGE_REQUIRED. Set from the login / OTP /
   * TOTP response and from every refresh (`TokenResponse.mustChangePassword`),
   * so it survives reloads. Absent = false.
   */
  mustChangePassword?: boolean;
}

/**
 * Canonical unauthenticated snapshot. Consumers (Plan 05 React surface) use this
 * as the default state before the first bootstrap probe resolves.
 */
export const UNAUTH_SNAPSHOT: AuthSnapshot = {
  isAuthenticated: false,
  user: null,
  userId: null,
  roles: [],
  tenantId: null,
  aud: null,
  accessToken: null,
  effectivePermissions: undefined,
};
